import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { UtensilsCrossed, Sun, Moon, LogOut, LayoutDashboard, Users, Building2, Receipt, PieChart, UserRound, Truck, Waves, UserCog, KeyRound, ScrollText, Zap, BookOpen, Wallet, Percent, Route } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { trDateLong } from "@/lib/api";
import api from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";
import GlobalSearch from "@/components/GlobalSearch";
import UsersDialog from "@/components/UsersDialog";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";

const TABS = [
  { id: "dashboard", label: "Genel Bakış & Ciro", icon: LayoutDashboard, testId: "nav-tab-dashboard" },
  { id: "quick", label: "Hızlı Giriş", icon: Zap, testId: "nav-tab-quick" },
  { id: "daily-entry", label: "Günlük Kişi Girişi", icon: Users, testId: "nav-tab-daily-entry" },
  { id: "customers", label: "Müşteriler", icon: Building2, testId: "nav-tab-customers" },
  { id: "menus", label: "Menüler", icon: BookOpen, testId: "nav-tab-menus" },
  { id: "shipment", label: "Sevkiyat", icon: Route, testId: "nav-tab-shipment" },
  { id: "ledger", label: "Cari Hesaplar", icon: Receipt, testId: "nav-tab-ledger" },
  { id: "cashbook", label: "Kasa Defteri", icon: Wallet, testId: "nav-tab-cashbook" },
  { id: "costs", label: "Maliyet & Kâr/Zarar", icon: PieChart, testId: "nav-tab-costs", admin: true },
  { id: "vat", label: "KDV", icon: Percent, testId: "nav-tab-vat", admin: true },
  { id: "staff", label: "Personel Cari", icon: UserRound, testId: "nav-tab-staff" },
  { id: "suppliers", label: "Tedarikçi Cari", icon: Truck, testId: "nav-tab-suppliers" },
  { id: "cashflow", label: "Nakit Akışı", icon: Waves, testId: "nav-tab-cashflow", admin: true },
  { id: "audit", label: "İşlem Günlüğü", icon: ScrollText, testId: "nav-tab-audit", admin: true },
];

export default function Shell({ tab, setTab, dark, setDark, children }) {
  const { user, logout } = useAuth();
  const [usersOpen, setUsersOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  const tabs = TABS.filter((t) => !t.admin || isAdmin);
  const [company, setCompany] = useState("");
  useEffect(() => { api.get("/settings/company").then((r) => setCompany(r.data.name)).catch(() => {}); }, [tab]);

  return (
    <div className="min-h-screen bg-background grain">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-card/85 border-b border-border">
        <div className="px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center text-primary-foreground shadow-lg shadow-primary/25">
              <UtensilsCrossed className="h-5 w-5" />
            </div>
            <div className="hidden sm:block">
              <div className="font-heading font-extrabold text-lg tracking-tight leading-none">ÇANKAYA CATERİNG</div>
              <div className="text-[9px] font-semibold uppercase tracking-widest text-muted-foreground mt-0.5" data-testid="company-name">
                {company || "Catering ERP"}
              </div>
            </div>
          </div>

          <GlobalSearch goTo={setTab} />
          <div className="hidden xl:block text-sm text-muted-foreground font-medium capitalize">
            {trDateLong(new Date())}
          </div>

          <div className="flex items-center gap-2">
            <NotificationBell goTo={setTab} />
            {isAdmin && (
              <button onClick={() => setUsersOpen(true)} data-testid="users-button" title="Kullanıcılar"
                className="h-9 w-9 rounded-lg border border-border flex items-center justify-center hover:bg-accent transition-colors">
                <UserCog className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={() => setDark(!dark)}
              data-testid="theme-toggle-button"
              className="h-9 w-9 rounded-lg border border-border flex items-center justify-center hover:bg-accent transition-colors"
            >
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <div className="hidden sm:flex flex-col items-end mr-1">
              <span className="text-sm font-semibold leading-none">{user?.name || "Kullanıcı"}</span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
                {user?.role === "admin" ? "Yönetici" : "Veri Giriş"}
              </span>
            </div>
            <button
              onClick={() => setPwOpen(true)}
              data-testid="change-password-button"
              title="Şifre Değiştir"
              className="h-9 w-9 rounded-lg border border-border flex items-center justify-center hover:bg-accent transition-colors"
            >
              <KeyRound className="h-4 w-4" />
            </button>
            <button
              onClick={logout}
              data-testid="logout-button"
              className="h-9 w-9 rounded-lg border border-border flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition-colors"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>

        <nav className="px-2 sm:px-4 flex items-center gap-1 overflow-x-auto border-t border-border/60">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                data-testid={t.testId}
                onClick={() => setTab(t.id)}
                className={`relative flex items-center gap-2 px-3.5 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                  active ? "text-primary" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Icon className="h-4 w-4" />
                {t.label}
                {active && (
                  <motion.div
                    layoutId="tab-underline"
                    className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary rounded-full"
                  />
                )}
              </button>
            );
          })}
        </nav>
      </header>

      <main className="px-4 sm:px-6 py-6 max-w-[1500px] mx-auto relative z-10">{children}</main>
      {isAdmin && <UsersDialog open={usersOpen} onClose={() => setUsersOpen(false)} me={user} />}
      <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
    </div>
  );
}
